import {Component, Input, OnInit, OnDestroy} from '@angular/core';
import {MessageService} from 'primeng/api';
import {
  CruiseControlSelectionCriteriaInterface
} from '../../../../../model/cruise-control-selection-criteria.interface';
import {CruiseControlSettingsInterface} from '../../../../../model/cruise-control-settings.interface';
import {CruiseControlStatusInterface} from '../../../../../model/cruise-control-status.interface';
import {CruiseControlViewState} from '../../../../../model/cruise-control-view-state.interface';
import {CruiseControlService} from '../../../../../service/cruise-control.service';
import {UserService} from '../../../../../service/user.service';
import {repeat, Subject, takeUntil} from 'rxjs';
import {
  CruiseControlCommandState,
  CruiseControlLocalRunPhase,
  cruiseControlCommandState,
  cruiseControlLocalRunPhase,
  getCommandState,
  getLatestResultText,
  getLatestRunDate,
  isBackendCommandActive,
  isNoCommandHistoryStatus,
  isNoCommandResponseError,
  latestResultClassByCommandState,
  normalizeCruiseControlStatus,
  statusClassByCommandState,
  statusPillVisibilityByCommandState
} from './cruise-control-form-state.utils';

const statusLabelByCommandState: Record<CruiseControlCommandState, string> = {
  [cruiseControlCommandState.idle]: '--',
  [cruiseControlCommandState.queued]: 'Queued',
  [cruiseControlCommandState.inProgress]: 'In Progress',
  [cruiseControlCommandState.done]: 'Done',
  [cruiseControlCommandState.failed]: 'Failed',
  [cruiseControlCommandState.cancelled]: 'Cancelled'
};

const completedCommandStates: CruiseControlCommandState[] = [
  cruiseControlCommandState.done,
  cruiseControlCommandState.failed,
  cruiseControlCommandState.cancelled
];

@Component({
  selector: 'auto-cruise-control-form-redesign',
  templateUrl: './cruise-control-form-redesign.component.html',
  styleUrl: './cruise-control-form-redesign.component.scss',
  standalone: false
})
export class CruiseControlFormRedesignComponent implements OnInit, OnDestroy {
  readonly cruiseControlService: CruiseControlService;
  readonly messageService: MessageService;
  readonly userService: UserService;
  @Input() accountId: number;
  @Input() settings: CruiseControlSettingsInterface;
  @Input() label: string;
  readonly placeholderValue: string = '--';
  readonly runButtonDefaultLabel: string = 'Run Now';
  readonly runButtonQueuedLabel: string = 'Queuing';
  readonly runButtonRunningLabel: string = 'Running';
  readonly runButtonSpinnerIcon: string = 'pi pi-spin pi-spinner';
  readonly latestResultSuccessText: string = 'All offer sets are up to date.';
  readonly latestResultFailedText: string = 'Run failed — no changes were applied';
  readonly defaultOfferSetStatusOption: boolean = false;
  readonly defaultCreateInventoryOnlyOfferSetsOption: boolean = true;
  readonly defaultUpdateOfferSetsVisibilityOption: boolean = false;
  readonly defaultFullVisibilityOfferSetScopeOption: number = 2;
  readonly vehicleFullVisibilityOfferSetScopeOption: number = 3;
  readonly defaultDealerAddOnOfferSetActiveOption: boolean = false;
  readonly offerSetVisibilityScopeFeatureName: string = 'offerSetVisibilityScope';
  readonly dealerAddOnOfferSetStatusFeatureName: string = 'ccDealerAddOnOfferSetStatus';
  generateAsOptions: object[] = [{value: false, label: 'Active'}, {value: true, label: 'Inactive'}]
  dealerAddOnOfferSetActiveOptions: object[] = [{value: true, label: 'Active'}, {value: false, label: 'Inactive'}]
  generateAllInventoryOptions: object[] = [{value: true, label: 'On'}, {value: false, label: 'Off'}]
  buildByOptions: object[] = [{value: 1, label: 'Model'}, {value: 2, label: 'Trim'}, {value: 3, label: 'Vehicle'}]
  selectionCriteria: CruiseControlSelectionCriteriaInterface[] = [];
  minimumImageOptions: object[] = []
  status: CruiseControlStatusInterface | null = null;
  isStatusInitialized: boolean = false;
  readonly statusPollingIntervalMs: number = 3000;
  readonly initialStatusPollDelayMs: number = 1500;
  pollingSubject: Subject<any> = new Subject<any>();
  destroySubject: Subject<void> = new Subject<void>();
  statusPollingTimeout: ReturnType<typeof setTimeout> | null = null;
  localRunPhase: CruiseControlLocalRunPhase = cruiseControlLocalRunPhase.idle;
  offerSetVisibilityScopeEnabled: boolean = false;
  dealerAddOnOfferSetActiveEnabled: boolean = false;
  lastCompletionToastCommandCreated: string | null = null;
  viewState: CruiseControlViewState = this.createDefaultViewState();
  readonly newLabel: string = 'New';

  constructor(cruiseControlService: CruiseControlService, messageService: MessageService, userService: UserService) {
    this.cruiseControlService = cruiseControlService;
    this.messageService = messageService;
    this.userService = userService;
  }
  /**
   * Init component
   */
  ngOnInit(): void {
    this.normalizeSettings();
    this.refreshViewState();
    this.populateSelectionCriteria();
    this.populateMinimumImageRequirementOptions();
    this.updateStatus();
    this.checkFeatureFlags();
  }
  /**
   * Cleanup component
   */
  ngOnDestroy(): void {
    this.cancelStatusPolling();
    this.pollingSubject.complete();
    this.destroySubject.next();
    this.destroySubject.complete();
  }
  /**
   * Checks feature flags for the account
   */
  checkFeatureFlags(): void {
    this.userService.getMpopFeature(this.offerSetVisibilityScopeFeatureName)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (features) => {
          const feature = features[0];
          if (feature) {
            this.offerSetVisibilityScopeEnabled = feature.enabled;
          }
        },
        error: () => this.offerSetVisibilityScopeEnabled = false
      });

    this.userService.getMpopFeature(this.dealerAddOnOfferSetStatusFeatureName)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (features) => {
          const feature = features[0];
          if (feature) {
            this.dealerAddOnOfferSetActiveEnabled = feature.enabled;
          }
        },
        error: () => this.dealerAddOnOfferSetActiveEnabled = false
      });
  }
  /**
   * Applies defaults required for segmented controls and dealer add-on setting
   */
  normalizeSettings(): void {
    if (!this.settings) {
      this.refreshViewState();

      return;
    }

    if (this.settings.generateAsPreview === null || this.settings.generateAsPreview === undefined) {
      this.settings.generateAsPreview = this.defaultOfferSetStatusOption;
    }
    if (this.settings.generateAllInventory === null || this.settings.generateAllInventory === undefined) {
      this.settings.generateAllInventory = this.defaultCreateInventoryOnlyOfferSetsOption;
    }
    if (this.settings.updateOfferSetsVisibility === null || this.settings.updateOfferSetsVisibility === undefined) {
      this.settings.updateOfferSetsVisibility = this.defaultUpdateOfferSetsVisibilityOption;
    }
    if (!this.settings.cruiseControlCreateLevelId) {
      this.settings.cruiseControlCreateLevelId = this.defaultFullVisibilityOfferSetScopeOption;
    }
    if (this.settings.dealerAddOnOfferSetActive === null || this.settings.dealerAddOnOfferSetActive === undefined) {
      this.settings.dealerAddOnOfferSetActive = this.defaultDealerAddOnOfferSetActiveOption;
    }

    this.normalizeUpdateOfferSetsVisibility();

    this.refreshViewState();
  }

  isUpdateOfferSetsVisibilityVisible(): boolean
  {
    let isVisible: boolean = false;

    if (this.settings && this.settings.generateAllInventory && !this.isVehicleScopeSelected()) {
      isVisible = true;
    }

    return isVisible;
  }

  isVehicleScopeSelected(): boolean
  {
    let isVehicleScopeSelected: boolean = false;

    if (this.settings && this.settings.cruiseControlCreateLevelId === this.vehicleFullVisibilityOfferSetScopeOption) {
      isVehicleScopeSelected = true;
    }

    return isVehicleScopeSelected;
  }

  handleGenerateAllInventoryChange(): void
  {
    if (this.settings) {
      this.settings.updateOfferSetsVisibility = false;

      if (this.settings.generateAllInventory && !this.isVehicleScopeSelected()) {
        this.settings.updateOfferSetsVisibility = true;
      }
    }

    this.updateSettings();
  }

  handleCruiseControlCreateLevelChange(): void
  {
    this.normalizeUpdateOfferSetsVisibility();
    this.updateSettings();
  }

  normalizeUpdateOfferSetsVisibility(): void
  {
    if (this.settings && !this.isUpdateOfferSetsVisibilityVisible()) {
      this.settings.updateOfferSetsVisibility = false;
    }
  }

  /**
   * Populates options for the minimum image requirements form field
   */
  populateMinimumImageRequirementOptions(): void {
    for (let i = 0; i < 21; i++) {
      this.minimumImageOptions.push({ value: i, label: i });
    }
  }
  /**
   * Populates the vehicle selection criteria options for the Vehicle Selection Criteria form field
   */
  populateSelectionCriteria(): void {
    this.cruiseControlService.getSelectionCriteria()
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (list) => {
          for (const item of list) {
            if (this.settings.vehicleCondition === item.vehicleCondition) {
              this.selectionCriteria.push(item);
            }
          }
        },
        error: () => this.messageService.add({severity: 'error', summary: 'Error', detail: 'Unable to retrieve vehicle selection criteria'})
      });
  }
  /**
   * Updates the Cruise Control command status on page load
   */
  updateStatus(): void {
    this.cruiseControlService.getStatus(this.accountId, this.settings.vehicleCondition)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (status) => this.handleStatusLoadSuccess(status),
        error: (error) => this.handleStatusLoadError(error)
      });
  }

  /**
   * Applies successful status load result
   */
  handleStatusLoadSuccess(status: CruiseControlStatusInterface | null | undefined): void {
    const normalizedStatus: CruiseControlStatusInterface = this.normalizeStatus(status);
    const hasActiveCommand: boolean = isBackendCommandActive(normalizedStatus);
    this.status = normalizedStatus;
    this.isStatusInitialized = true;
    this.syncLocalRunPhaseWithBackendActivity(hasActiveCommand);

    if (hasActiveCommand) {
      this.startStatusPolling();
      this.messageService.add({severity: 'info', detail: `Cruise Control is currently running for ${this.label.toLowerCase()} inventory`});
    }

    if (!hasActiveCommand && this.localRunPhase !== cruiseControlLocalRunPhase.queuing) {
      this.localRunPhase = cruiseControlLocalRunPhase.idle;
    }

    this.refreshViewState();
  }

  /**
   * Handles status-load error payloads
   */
  handleStatusLoadError(error: unknown): void {
    const isNoCommandResponse: boolean = isNoCommandResponseError(error);

    if (isNoCommandResponse) {
      this.handleNoCommandStatus(true);
    }

    if (!isNoCommandResponse) {
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Unable to retrieve Cruise Control command status'});
    }

    this.refreshViewState();
  }

  /**
   * Resets component state to an initialized no-command status
   */
  handleNoCommandStatus(shouldCancelPolling: boolean): void {
    this.status = this.createEmptyStatus();
    this.isStatusInitialized = true;
    this.localRunPhase = cruiseControlLocalRunPhase.idle;

    if (shouldCancelPolling) {
      this.cancelStatusPolling();
    }
  }

  /**
   * Recomputes a single canonical view state used by the template
   */
  refreshViewState(): void {
    const statusForDisplay: CruiseControlStatusInterface = this.getStatusForDisplay();
    let commandState: CruiseControlCommandState = cruiseControlCommandState.idle;

    if (this.isStatusInitialized) {
      commandState = getCommandState(statusForDisplay);
    }

    const hasBackendActiveCommand: boolean = isBackendCommandActive(statusForDisplay);
    const hasLocalActiveCommand: boolean = this.localRunPhase !== cruiseControlLocalRunPhase.idle;
    const isCommandActive: boolean = hasBackendActiveCommand || hasLocalActiveCommand;
    const runButtonLabel: string = this.getRunButtonLabel(commandState);
    const runButtonIcon: string = this.getRunButtonIcon(commandState);
    const runButtonDisabled: boolean = this.isRunButtonDisabled(commandState);
    let statusClass: string = statusClassByCommandState[cruiseControlCommandState.idle];
    let statusLabel: string = this.placeholderValue;
    let isStatusPillVisible: boolean = false;
    let latestResultClass: string = latestResultClassByCommandState[cruiseControlCommandState.idle];
    let latestResultText: string = this.placeholderValue;
    let latestRunDate: string = this.placeholderValue;

    if (this.isStatusInitialized) {
      statusClass = statusClassByCommandState[commandState];
      statusLabel = statusLabelByCommandState[commandState];
      isStatusPillVisible = statusPillVisibilityByCommandState[commandState];
      latestResultClass = latestResultClassByCommandState[commandState];
      latestResultText = getLatestResultText(
        commandState,
        statusForDisplay,
        this.placeholderValue,
        this.latestResultSuccessText,
        this.latestResultFailedText
      );
      latestRunDate = getLatestRunDate(statusForDisplay, this.placeholderValue);
    }

    this.viewState = {
      commandState,
      isCommandActive,
      runButtonLabel,
      runButtonIcon,
      runButtonDisabled,
      statusLabel,
      statusClass,
      isStatusPillVisible,
      latestResultText,
      latestResultClass,
      latestRunDate
    };
  }

  /**
   * Gets current status object or a safe empty fallback for view-state derivation
   */
  getStatusForDisplay(): CruiseControlStatusInterface {
    let statusForDisplay: CruiseControlStatusInterface = this.createEmptyStatus();

    if (this.status) {
      statusForDisplay = this.status;
    }


    return statusForDisplay;
  }
  /**
   * Gets run button label from local and backend activity state
   */
  getRunButtonLabel(commandState: CruiseControlCommandState): string {
    let runButtonLabel: string = this.runButtonDefaultLabel;

    if (this.localRunPhase === cruiseControlLocalRunPhase.queuing) {
      runButtonLabel = this.runButtonQueuedLabel;
    }

    if (commandState === cruiseControlCommandState.inProgress) {
      runButtonLabel = this.runButtonRunningLabel;
    }


    return runButtonLabel;
  }
  /**
   * Gets run button icon from activity state
   */
  getRunButtonIcon(commandState: CruiseControlCommandState): string {
    let runButtonIcon: string = '';

    if (this.localRunPhase === cruiseControlLocalRunPhase.queuing) {
      runButtonIcon = this.runButtonSpinnerIcon;
    }

    if (commandState === cruiseControlCommandState.inProgress) {
      runButtonIcon = this.runButtonSpinnerIcon;
    }


    return runButtonIcon;
  }
  /**
   * Determines if run button should be disabled
   */
  isRunButtonDisabled(commandState: CruiseControlCommandState): boolean {
    let isDisabled: boolean = false;

    if (!this.isStatusInitialized) {
      isDisabled = true;
    }

    if (!this.settings || this.settings.enabled === false) {
      isDisabled = true;
    }

    if (commandState === cruiseControlCommandState.inProgress) {
      isDisabled = true;
    }


    return isDisabled;
  }
  /**
   * Aligns local run phase with backend active status
   */
  syncLocalRunPhaseWithBackendActivity(hasActiveCommand: boolean): void {
    if (hasActiveCommand && this.localRunPhase !== cruiseControlLocalRunPhase.running) {
      this.localRunPhase = cruiseControlLocalRunPhase.running;
    }

    if (!hasActiveCommand && this.localRunPhase === cruiseControlLocalRunPhase.running) {
      this.localRunPhase = cruiseControlLocalRunPhase.idle;
    }
  }

  /**
   * Determines if command state is completed
   */
  isCompletedState(commandState: CruiseControlCommandState): boolean {
    let isCompleted: boolean = false;

    if (completedCommandStates.includes(commandState)) {
      isCompleted = true;
    }

    return isCompleted;
  }
  /**
   * Creates the default template view state
   */
  createDefaultViewState(): CruiseControlViewState {
    return {
      commandState: cruiseControlCommandState.idle,
      isCommandActive: false,
      runButtonLabel: this.runButtonDefaultLabel,
      runButtonIcon: '',
      runButtonDisabled: true,
      statusLabel: this.placeholderValue,
      statusClass: 'result-placeholder',
      isStatusPillVisible: false,
      latestResultText: this.placeholderValue,
      latestResultClass: 'result-placeholder',
      latestRunDate: this.placeholderValue
    };
  }
  /**
   * Saves the changed settings values
   */
  updateSettings(): void {
    this.normalizeSettings();
    this.cruiseControlService.updateSettings(this.settings)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => this.messageService.add({
          severity: 'success', summary: 'Success', detail: 'Settings were updated'}),
        error: () => this.messageService.add({
          severity: 'error', summary: 'Error', detail: 'Failed to save settings'})
      });
  }
  /**
   * Starts a new Cruise Control command
   */
  startCruiseControl(): void {
    this.localRunPhase = cruiseControlLocalRunPhase.queuing;
    this.refreshViewState();

    this.messageService.add({
      severity: 'info', summary: 'Info', detail: `Starting Cruise Control for ${this.label.toLowerCase()} offer sets`});
    this.cruiseControlService.run(this.accountId, this.settings.vehicleCondition)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          this.refreshViewState();
          this.startStatusPolling(this.initialStatusPollDelayMs);
        },
        error: () => {
          this.localRunPhase = cruiseControlLocalRunPhase.idle;
          this.cancelStatusPolling();
          this.refreshViewState();

          this.messageService.add({
            severity: 'error', summary: 'Error', detail: 'Cruise Control ran into an issue and could not complete. View status for more details.'});
        }
      });
  }
  /**
   * Polls the status of the most recent Cruise Control command
   */
  startStatusPolling(initialDelayMs: number = 0): void {
    this.cancelStatusPolling();

    const startPolling = (): void => this.beginStatusPollingSubscription();

    if (initialDelayMs > 0) {
      this.statusPollingTimeout = setTimeout(() => {
        startPolling();
      }, initialDelayMs);
    } else {
      startPolling();
    }
  }

  /**
   * Starts the status polling stream subscription
   */
  beginStatusPollingSubscription(): void {
    this.cruiseControlService.getStatus(this.accountId, this.settings.vehicleCondition)
      .pipe(
        repeat({delay: this.statusPollingIntervalMs}),
        takeUntil(this.pollingSubject),
        takeUntil(this.destroySubject)
      )
      .subscribe({
        next: (status) => this.handlePollingStatusUpdate(status),
        error: (error) => this.handlePollingStatusError(error)
      });
  }

  /**
   * Applies successful polling status updates
   */
  handlePollingStatusUpdate(status: CruiseControlStatusInterface | null | undefined): void {
    const normalizedStatus: CruiseControlStatusInterface = this.normalizeStatus(status);
    const hasActiveCommand: boolean = isBackendCommandActive(normalizedStatus);
    const hasNoCommandHistory: boolean = isNoCommandHistoryStatus(normalizedStatus);
    const wasActiveCommand: boolean = isBackendCommandActive(this.status);
    const hasLocalActivity: boolean = this.localRunPhase !== cruiseControlLocalRunPhase.idle;
    const shouldPreservePreviousStatus: boolean = hasNoCommandHistory && !hasActiveCommand && (hasLocalActivity || wasActiveCommand);

    if (!shouldPreservePreviousStatus) {
      this.status = normalizedStatus;
    }

    this.isStatusInitialized = true;

    if (hasActiveCommand) {
      this.syncLocalRunPhaseWithBackendActivity(true);
    }

    if (!hasActiveCommand && !shouldPreservePreviousStatus) {
      this.localRunPhase = cruiseControlLocalRunPhase.idle;
    }

    this.refreshViewState();

    const commandState: CruiseControlCommandState = this.viewState.commandState;
    if (this.isCompletedState(commandState)) {
      this.localRunPhase = cruiseControlLocalRunPhase.idle;
      this.cancelStatusPolling();
      this.refreshViewState();
    }

    if (commandState === cruiseControlCommandState.done && normalizedStatus.created !== this.lastCompletionToastCommandCreated) {
      this.lastCompletionToastCommandCreated = normalizedStatus.created;
      this.messageService.add({
        severity: 'success', summary: 'Success', detail: 'Cruise Control completed'});
    }
  }

  /**
   * Handles polling errors and resets state
   */
  handlePollingStatusError(error: unknown): void {
    const isNoCommandResponse: boolean = isNoCommandResponseError(error);
    this.localRunPhase = cruiseControlLocalRunPhase.idle;
    this.cancelStatusPolling();

    if (isNoCommandResponse) {
      this.handleNoCommandStatus(false);
    }

    this.refreshViewState();
  }

  /**
   * Normalizes status payload values and applies defaults for missing/no-command states
   */
  normalizeStatus(status: CruiseControlStatusInterface | null | undefined): CruiseControlStatusInterface {
    const emptyStatus: CruiseControlStatusInterface = this.createEmptyStatus();


    return normalizeCruiseControlStatus(status, emptyStatus);
  }

  /**
   * Creates an empty status payload used for accounts without command history
   */
  createEmptyStatus(): CruiseControlStatusInterface {
    let vehicleCondition: string = '';

    if (this.settings && this.settings.vehicleCondition) {
      vehicleCondition = this.settings.vehicleCondition;
    }

    return {
      accountId: this.accountId,
      vehicleCondition,
      started: null,
      created: null,
      completed: null,
      result: null,
      canceled: false,
      pending: false,
      adsCreated: null,
      manualProcess: false
    };
  }

  /**
   * Stops polling the status of the most recent Cruise Control command
   */
  cancelStatusPolling(): void {
    if (this.statusPollingTimeout !== null) {
      clearTimeout(this.statusPollingTimeout);
      this.statusPollingTimeout = null;
    }

    this.pollingSubject.next(true);
  }
}
